import React from "react";
import { put } from "../../../utils/api";
import GenericViewDrawer1 from "../GenericViewDrawer1";
import { formatUnitOfProductSoldFromRow, formatValueUnitField } from "./approvalTableFormatters";

/** Show calendar dates as YYYY-MM-DD (matches form / editValue), not full ISO strings. */
const formatDisplayValue = (val) => {
  if (val === undefined || val === null) return "—";
  if (val instanceof Date && !Number.isNaN(val.getTime())) {
    return val.toISOString().split("T")[0];
  }
  if (typeof val === "object") {
    try {
      return JSON.stringify(val);
    } catch {
      return "—";
    }
  }
  if (typeof val === "string") {
    const trimmed = val.trim();
    if (!trimmed) return "—";
    // ISO datetime or date-only: 2026-05-01T00:00:00.000Z or 2026-05-01
    const isoDate = trimmed.match(/^(\d{4}-\d{2}-\d{2})(?:T[\d:.]+(?:Z|[+-]\d{2}:?\d{2})?)?$/i);
    if (isoDate) return isoDate[1];
    return trimmed;
  }
  return String(val);
};

/** Per-field display for approval diff cells (objects that would otherwise stringify as JSON). */
const formatEditTableCell = (key, val, data) => {
  if (key === "fuelEntries" && Array.isArray(val) && val.length === 0) {
    return "—";
  }
  if (key === "unitOfProductSold") {
    if (val === undefined || val === null || val === "") return "—";
    if (typeof val === "object") {
      const row = {
        unitOfProductSold: val,
        masterProduct: data?.product ?? data?.masterProduct ?? data?.productMaster,
      };
      const s = formatUnitOfProductSoldFromRow(row);
      return s === "N/A" ? "—" : s;
    }
    const unit = data?.unitOfProductSold?.unit ?? data?.editValue?.unitOfProductSold?.unit ?? "";
    const s = formatValueUnitField({ value: val, unit });
    return s === "N/A" ? "—" : s;
  }
  return formatDisplayValue(val);
};

/**
 * editValue uses form field names; stored rows may use different keys (e.g. `country` vs `locationCountry`).
 * Resolve "previous" display values when keys do not match.
 */
const getPreviousValueForEditKey = (data, key) => {
  if (key === "locationCountry") {
    return (
      data?.locationCountry ??
      data?.country ??
      data?.asset?.locationCountry ??
      data?.assetId?.locationCountry
    );
  }
  if (key === "assetId") {
    return (
      data?.assetCode ??
      data?.asset?.assetId ??
      data?.lam?.assetId ??
      data?.assetId?.assetId ??
      data?.assetId
    );
  }
  if (key === "productMaster") {
    const pm =
      data?.productMaster && typeof data.productMaster === "object"
        ? data.productMaster
        : null;
    return (
      data?.productName ??
      data?.product?.productName ??
      pm?.productName ??
      data?.editProductName ??
      (typeof data?.productMaster === "string" ? data.productMaster : null)
    );
  }
  if (key === "franchiseMaster") {
    const fm =
      data?.franchiseMaster && typeof data.franchiseMaster === "object"
        ? data.franchiseMaster
        : null;
    return (
      data?.franchiseName ??
      data?.franchiseMasterData?.franchiseName ??
      fm?.franchiseName ??
      (typeof data?.franchiseMaster === "string" ? data.franchiseMaster : null)
    );
  }
  if (key === "investmentMaster") {
    const im =
      data?.investmentMaster && typeof data.investmentMaster === "object"
        ? data.investmentMaster
        : null;
    return (
      data?.investeeProjectName ??
      data?.investmentMasterData?.investeeProjectName ??
      im?.investeeProjectName ??
      (typeof data?.investmentMaster === "string" ? data.investmentMaster : null)
    );
  }
  return data?.[key];
};

/** Requested column: editValue may store Mongo id while API adds human-readable codes on the row or nested assetData. */
const getRequestedValueForEditKey = (data, key, ev) => {
  if (key === "assetId") {
    return (
      ev?.editAssetCode ??
      data?.editAssetCode ??
      ev?.assetData?.assetId ??
      ev?.assetId
    );
  }
  if (key === "productMaster") {
    return (
      data?.editProductName ??
      ev?.productData?.productName ??
      data?.productName ??
      data?.product?.productName ??
      (ev?.productMaster && typeof ev.productMaster === "object"
        ? ev.productMaster?.productName
        : null) ??
      ev?.productMaster
    );
  }
  if (key === "franchiseMaster") {
    const evFm =
      ev?.franchiseMaster && typeof ev.franchiseMaster === "object"
        ? ev.franchiseMaster
        : null;
    return (
      data?.editFranchiseName ??
      ev?.franchiseMasterData?.franchiseName ??
      evFm?.franchiseName ??
      (typeof ev?.franchiseMaster === "string" ? ev.franchiseMaster : null)
    );
  }
  if (key === "investmentMaster") {
    const evIm =
      ev?.investmentMaster && typeof ev.investmentMaster === "object"
        ? ev.investmentMaster
        : null;
    return (
      data?.editMasterInvesteeProjectName ??
      ev?.investmentMasterData?.investeeProjectName ??
      evIm?.investeeProjectName ??
      (typeof ev?.investmentMaster === "string" ? ev.investmentMaster : null)
    );
  }
  return ev?.[key];
};

/**
 * Human-readable Field column labels for `editValue` keys.
 * Falls back to productMaster / franchiseMaster / investmentMaster or Title Case from key.
 */
const FIELD_LABELS = {
  replacementParts: "Recyclable Components (%)",
};

/** Keys on editValue that are shown elsewhere or are internal / workflow metadata — omit from the Field / Previous / Requested table. */
const EDIT_VALUE_KEYS_OMIT_FROM_TABLE = new Set([
  "documents", // Documents row with DocumentItem
  "assetData", // populated master snapshot
  "productData", // EOL populated product snapshot (avoid raw JSON row)
  "franchiseMasterData", // populated franchise snapshot (avoid raw JSON row)
  "investmentMasterData", // populated investment master snapshot (avoid raw JSON row)
  "remark", // optional free text; omit from diff grid
  "editRemarkReason", // shown under table as "Remark Reason"
  "requestStatus",
  "requestMode",
  "approvalStatus",
]);


const buildFieldsFromEditValue = (data, extraOmitKeys = []) => {
  const omitKeys = new Set([
    ...EDIT_VALUE_KEYS_OMIT_FROM_TABLE,
    ...(Array.isArray(extraOmitKeys) ? extraOmitKeys : []),
  ]);
  const ev = data?.editValue;
  if (!ev || typeof ev !== "object") return [];
  return Object.keys(ev)
  .filter((key) => !omitKeys.has(key))
  .map((key) => ({
    label:
      FIELD_LABELS[key] ||
      (key === "productMaster"
        ? "Product Name"
        : key === "franchiseMaster"
        ? "Franchise Name"
        : key === "investmentMaster"
        ? "Investee / Project"
        : key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (s) => s.toUpperCase())
            .trim()),
    previous: formatEditTableCell(
      key,
      getPreviousValueForEditKey(data, key),
      data
    ),
    requested: formatEditTableCell(
      key,
      getRequestedValueForEditKey(data, key, ev),
      data
    ),
  }));
};

const SimpleScope3ApprovalDrawer = ({
  open,
  onClose,
  data,
  setsnackOpen,
  fetchDataCallback,
  apiUrl,
  title = "Change request",
  /** Extra `editValue` keys to hide in the Field / Previous / Requested table (per page). */
  omitEditValueKeys,
}) => {
  if (!data) return null;

  const handleApprovalAction = async (action) => {
    try {
      const response = await put(`${apiUrl}/${data._id}`, {
        approvalStatus: action === "approve" ? "Accepted" : "Rejected",
      });
      if (response?.data) {
        setsnackOpen({
          open: true,
          message: response.data.message,
          severity: "success",
        });
        fetchDataCallback();
        onClose();
      }
    } catch (error) {
      setsnackOpen({
        open: true,
        message: error.message || "An error occurred",
        severity: "error",
      });
    }
  };

  const fields = buildFieldsFromEditValue(data, omitEditValueKeys);
  return (
    <GenericViewDrawer1
      open={open}
      onClose={onClose}
      title={title}
      fields={fields}
      documentsField={
        data?.documents || data?.editValue?.documents
          ? {
            label: "Documents",
            previous: data?.documents,
            requested: data?.editValue?.documents,
          }
          : null
      }
      timeline={data?.timeLine}
      editRemarkReason={data?.editRemarkReason}
      onApprove={() => handleApprovalAction("approve")}
      onReject={() => handleApprovalAction("reject")}
    />
  );
};

export default SimpleScope3ApprovalDrawer;
